import React, { useCallback, useState } from 'react';
import { isAiMateUnifiedHelpIntegrationEnabled } from '@helpCenter/feature-flags';
import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { Inline } from '@atlaskit/primitives';
import { N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import messages from '../../pages/messages';
import { useActions, useConversation } from '../../store/converse';
import { getAnalyticsWebClient } from '../../utils/analytics';
import { Flipped } from '../../utils/flip-toolkit';
import type { ConversationInputUser$key } from './__generated__/ConversationInputUser.graphql';
import { Container, Form, IconButton, Input, InputWrapper } from './ConversationInput.styled';

export const ConversationInput: React.VFC<{
    userRef: ConversationInputUser$key;
}> = ({ userRef }) => {
    const [term, setTerm] = useState('');
    const { formatMessage } = useIntl();
    const [conversation] = useConversation();
    const {
        startNewConversation,
        addHelpseekerMessage,
        startNewConversationWithAiMate,
        addHelpseekerMessageWithAiMate,
    } = useActions();
    const analyticsClient = getAnalyticsWebClient();

    const userData = useFragment(
        graphql`
            fragment ConversationInputUser on User {
                accountId
            }
        `,
        userRef
    );

    const { accountId } = userData;

    const hasConversation = !!conversation;

    const onSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            // Suppressing existing violation. Please fix this.
            // eslint-disable-next-line no-shadow
            const term = (event.currentTarget.elements.namedItem('term') as HTMLInputElement).value.trim();

            if (term.length > 0) {
                if (isAiMateUnifiedHelpIntegrationEnabled()) {
                    if (hasConversation) {
                        addHelpseekerMessageWithAiMate(term, { accountId });
                    } else {
                        startNewConversationWithAiMate(term, { accountId });
                    }
                } else if (hasConversation) {
                    addHelpseekerMessage(term, { accountId });
                } else {
                    startNewConversation(term, { accountId });
                }

                setTerm('');
            }
        },
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [accountId, addHelpseekerMessage, hasConversation, startNewConversation]
    );

    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const handleChange = useCallback((event) => setTerm(event.currentTarget.value), []);

    const fireInputSelectedAnalytics = useCallback(() => {
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        analyticsClient.sendUIEvent({
            source: 'uhConversationScreen',
            action: 'clicked',
            actionSubject: 'input',
            actionSubjectId: 'conversationInput',
        });
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container isConversationView={!!conversation}>
            <Inline grow="fill" space="space.150" alignBlock="center">
                <Form onSubmit={onSubmit}>
                    <Flipped flipId="ConversationInput">
                        <InputWrapper isHighlighted={!conversation}>
                            <Input
                                type="text"
                                name="term"
                                autoComplete="off"
                                // Disabling existing violations, should be fixed when revisited.
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus
                                spellCheck={false}
                                placeholder={
                                    !conversation
                                        ? formatMessage(messages.unifiedHelpSearchPlaceholder)
                                        : formatMessage(messages.unifiedHelpSearchPlaceholderInConversation)
                                }
                                value={term}
                                onChange={handleChange}
                                onClick={fireInputSelectedAnalytics}
                                onFocus={fireInputSelectedAnalytics}
                            />
                            <IconButton type="submit">
                                <SearchIcon
                                    primaryColor={token('color.icon.subtle', N500)}
                                    label="Submit search query"
                                />
                            </IconButton>
                        </InputWrapper>
                    </Flipped>
                </Form>
            </Inline>
        </Container>
    );
};
